import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRestaurantId } from '../utils/restaurantContext';
import { useWaiterGame } from '../utils/waiterGameContext';
import { FaTrash, FaPlus, FaMinus, FaArrowLeft, FaShoppingCart } from 'react-icons/fa';
import { useCart } from '../utils/cartContext';
import { ordersAPI, restaurantAPI } from '../utils/api';
import WifiRequiredPopup from '../components/WifiRequiredPopup';
import PlaceOrderDialog from '../components/PlaceOrderDialog';
import LoadingSpinner from '../components/LoadingSpinner';
import './CartPage.css';

function CartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const restaurantId = useRestaurantId();
  const { openInvite } = useWaiterGame();
  const { cartItems, removeFromCart, updateQuantity, clearCart, getTotalPrice } = useCart();
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [maxTables, setMaxTables] = useState(null);

  useEffect(() => {
    if (!restaurantId) {
      navigate('/customer', { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await restaurantAPI.getById(restaurantId);
        const tables = Number(res?.data?.restaurant?.number_of_tables);
        if (Number.isInteger(tables) && tables > 0) setMaxTables(tables);
      } catch {
        // ignore
      }
    })();
  }, [restaurantId, navigate]);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wifiRequired, setWifiRequired] = useState(false);
  const [wifiClientIp, setWifiClientIp] = useState('');
  const [lastOrderData, setLastOrderData] = useState(null);

  const totalPrice = getTotalPrice();

  
  const navigateToMenu = () => {
    // Universal flow uses /menu/:restaurantId/menu
    if (location.pathname.startsWith('/menu/') && restaurantId) {
      navigate(`/menu/${encodeURIComponent(restaurantId)}/menu`);
      return;
    }

    // Legacy flow uses /customer/menu?restaurantId=...
    if (restaurantId) {
      navigate(`/customer/menu?restaurantId=${encodeURIComponent(restaurantId)}`);
    } else {
      navigate('/customer/menu');
    }
  };

  const handleQuantityChange = (dishId, change) => {
    const item = cartItems.find((item) => item.id === dishId);
    if (item) {
      const newQuantity = item.quantity + change;
      updateQuantity(dishId, newQuantity);
    }
  };

  const handlePlaceOrder = async () => {
    if (isSubmitting) return;

    if (cartItems.length === 0) {
      setNotification({
        type: 'error',
        message: 'Your cart is empty. Add some items first!',
      });
      return;
    }

    try {
      if (!restaurantId) {
        navigate('/customer', { replace: true });
        return;
      }
      const restaurantIdForOrder = restaurantId;
      const wifiRes = await restaurantAPI.getWifiStatus(restaurantIdForOrder);
      setWifiClientIp(wifiRes?.data?.clientIp || '');
      if (!wifiRes?.data?.allowed) {
        setWifiRequired(true);
        return;
      }

      setShowOrderDialog(true);
    } catch (e) {
      // If we can't verify, fall back to backend enforcement on submit.
      setShowOrderDialog(true);
    }
  };

  const handleOrderSubmit = async (orderData) => {
    if (isSubmitting) return;

    setLastOrderData(orderData);
    setIsSubmitting(true);
    try {
      if (!restaurantId) {
        navigate('/customer', { replace: true });
        return;
      }
      const restaurantIdForOrder = restaurantId;

      // Pre-check WiFi status before placing orders
      const wifiRes = await restaurantAPI.getWifiStatus(restaurantIdForOrder);
      if (!wifiRes?.data?.allowed) {
        setWifiRequired(true);
        return;
      }

      const parsedTable = Number(orderData.tableNumber);
      if (maxTables && (!Number.isInteger(parsedTable) || parsedTable < 1 || parsedTable > maxTables)) {
        setNotification({
          type: 'error',
          message: `Please enter a valid table number (1-${maxTables}).`,
        });
        return;
      }

      // Place orders for all items in cart
      const orderPromises = cartItems.map((item) =>
        ordersAPI.create({
          restaurantId: restaurantIdForOrder,
          customerName: orderData.name,
          mobile: orderData.mobile,
          tableNumber: parsedTable,
          dishId: item.id,
          dishData: {
            id: item.id,
            name: item.name,
            price: item.price,
            image: item.image,
          },
          quantity: item.quantity,
        })
      );

      await Promise.all(orderPromises);

      clearCart();
      setShowOrderDialog(false);
      setNotification({
        type: 'success',
        message: `Successfully placed ${cartItems.length} order(s)!`,
      });

      // Playful waiting-time engagement
      openInvite({ dishName: cartItems?.[0]?.name });

      // Redirect to menu after 2 seconds with restaurantId preserved
      setTimeout(() => {
        navigateToMenu();
      }, 2000);
    } catch (error) {
      console.error('Error placing orders:', error);

      if (error?.status === 403) {
        // Backend blocked ordering due to WiFi-only mode.
        setWifiClientIp('');
        setWifiRequired(true);
        return;
      }

      setNotification({
        type: 'error',
        message: error.message || 'Failed to place orders. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="cart-page">
      <WifiRequiredPopup
        isOpen={wifiRequired}
        clientIp={wifiClientIp}
        onClose={() => setWifiRequired(false)}
        onRetry={() => {
          if (!lastOrderData) return Promise.resolve();
          return handleOrderSubmit(lastOrderData);
        }}
        actionLabel="Order"
      />
      <header className="cart-header">
        <div className="cart-header-inner">
          <button className="back-button" onClick={navigateToMenu}>
            <FaArrowLeft /> Back to Menu
          </button>

          <div className="cart-title-row">
            <h1 className="cart-title">Your Cart</h1>
            <p className="cart-subtitle">
              Review items, adjust quantities, and place your order.
            </p>
          </div>
        </div>
      </header>

      <div className="cart-container">
        {notification && (
          <div className={`cart-notification ${notification.type}`}>
            <span>{notification.message}</span>
            <button
              type="button"
              className="cart-notification-close"
              onClick={() => setNotification(null)}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        )}

        {cartItems.length === 0 ? (
          <div className="empty-cart">
            <FaShoppingCart className="empty-cart-icon" />
            <h2>Your cart is empty</h2>
            <p>Add items from the menu to start your order.</p>
            <button className="browse-menu-btn" onClick={navigateToMenu}>
              Browse Menu
            </button>
          </div>
        ) : (
          <>
            <div className="cart-grid">
            <section className="cart-items-panel" aria-label="Cart items">
              <div className="cart-items-header">
                <h2 className="cart-items-title">Items</h2>
                <span className="cart-items-count">
                  {cartItems.reduce((sum, item) => sum + item.quantity, 0)} total
                </span>
              </div>

              <div className="cart-items">
                {cartItems.map((item) => (
                  <article key={item.id} className="cart-item">
                    <div className="cart-item-image">
                      <img
                        src={item.image}
                        alt={item.name}
                        onError={(e) => {
                          e.target.src =
                            'https://images.unsplash.com/photo-1601050690597-df0568f70950?w=400';
                        }}
                      />
                    </div>

                    <div className="cart-item-body">
                      <div className="cart-item-main">
                        <div className="cart-item-title">
                          <h3 className="cart-item-name">{item.name}</h3>
                          <p className="cart-item-unit-price">{formatPrice(item.price)}</p>
                        </div>

                        <div className="cart-item-actions">
                          <div className="quantity-controls" aria-label="Quantity controls">
                            <button
                              type="button"
                              className="quantity-btn"
                              onClick={() => handleQuantityChange(item.id, -1)}
                              aria-label="Decrease quantity"
                            >
                              <FaMinus />
                            </button>
                            <span className="quantity-value">{item.quantity}</span>
                            <button
                              type="button"
                              className="quantity-btn"
                              onClick={() => handleQuantityChange(item.id, 1)}
                              aria-label="Increase quantity"
                            >
                              <FaPlus />
                            </button>
                          </div>

                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeFromCart(item.id)}
                            aria-label="Remove item"
                            title="Remove"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>

                      <div className="cart-item-footer">
                        <span className="cart-item-total-label">Item total</span>
                        <span className="cart-item-total">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <aside className="cart-summary" aria-label="Order summary">
              <h2 className="cart-summary-title">Order Summary</h2>

              <div className="summary-row">
                <span className="summary-label">Total items</span>
                <span className="summary-value">
                  {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              </div>
              <div className="summary-row total">
                <span className="summary-label">Grand total</span>
                <span className="summary-value">{formatPrice(totalPrice)}</span>
              </div>

              <button
                className="place-order-btn"
                onClick={handlePlaceOrder}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Placing Orders…' : 'Place Order'}
              </button>

              <button className="browse-menu-link" type="button" onClick={navigateToMenu}>
                Add more items
              </button>
            </aside>
            </div>

            {/* Mobile sticky checkout bar */}
          <div className="cart-mobile-bar" role="region" aria-label="Checkout">
            <div className="cart-mobile-bar-total">
              <div className="cart-mobile-bar-label">Total</div>
              <div className="cart-mobile-bar-value">{formatPrice(totalPrice)}</div>
            </div>
            <button
              className="cart-mobile-bar-btn"
              onClick={handlePlaceOrder}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Placing…' : 'Place order'}
            </button>
          </div>
          </>
        )}
      </div>

      <PlaceOrderDialog
        isOpen={showOrderDialog}
        onClose={() => setShowOrderDialog(false)}
        cartItems={cartItems}
        maxTables={maxTables}
        submitting={isSubmitting}
        onPlaceOrder={handleOrderSubmit}
      />

      {isSubmitting ? <LoadingSpinner /> : null}
    </div>
  );
}

export default CartPage;

